import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { formatNOK, formatDate } from "@/lib/format";
import { useOffers, type OfferStatus, type OfferWithCustomer } from "@/hooks/use-offers";
import { OfferStatusBadge } from "@/components/offer/OfferStatusBadge";
import { OfferProbabilityBadge } from "@/components/offer/OfferProbabilityBadge";
import { OfferPipelineBoard } from "@/components/offer/OfferPipelineBoard";
import { NewOfferDialog } from "@/components/offer/NewOfferDialog";
import { ImportOfferDialog } from "@/components/offer/ImportOfferDialog";
import { SrIcon } from "@/components/ui/SrIcon";
import { useImpersonation } from "@/contexts/ImpersonationContext";
import { useFeatureFlag } from "@/hooks/use-feature-flag";
import { cn } from "@/lib/utils";
import { ClickableRow } from "@/components/shared/ClickableRow";

type TabKey = "all" | OfferStatus;

const TABS: Array<{ key: TabKey; label: string }> = [
  { key: "all", label: "Alle" },
  { key: "draft", label: "Utkast" },
  { key: "sent", label: "Sendt" },
  { key: "accepted", label: "Akseptert" },
  { key: "lost", label: "Tapt" },
];

function filterOffers(
  offers: OfferWithCustomer[],
  tab: TabKey,
  query: string,
  onlyMine: boolean,
  myUserId: string | null,
): OfferWithCustomer[] {
  const q = query.trim().toLowerCase();
  return offers.filter((o) => {
    if (tab !== "all" && o.status !== tab) return false;
    if (onlyMine && myUserId && o.created_by !== myUserId) return false;
    if (!q) return true;
    const customerName = o.customer?.name ?? o.customer_name ?? "";
    return (
      o.title.toLowerCase().includes(q) ||
      customerName.toLowerCase().includes(q)
    );
  });
}

type ViewMode = "table" | "board";

export default function OffersPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { data: offers, isLoading } = useOffers();
  const { activeUser } = useImpersonation();
  // Bare tilbudspipen styrer listen og kanban-visningen her. OfferEditPage leser i tillegg
  // feature_crm_ko_pipe (CRM-2.7) for sannsynlighet, close-dato og tapsårsak.
  const pipelineEnabled = useFeatureFlag("feature_offer_pipeline");
  const [tab, setTab] = useState<TabKey>("all");
  const [query, setQuery] = useState("");
  const [view, setView] = useState<ViewMode>("table");
  const [newDialogOpen, setNewDialogOpen] = useState(
    () => searchParams.get("nytt") !== null,
  );
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  // VIB-1304 smoke-test-fiks (Erik): visningen skal som standard vise KUN
  // innlogget selgers egne tilbud ("standardiser til innlogget person"), på
  // tvers av både tabell og board (delt onlyMine-state). Fravær av "mine"-
  // param = default PÅ; eksplisitt "0" = bevisst opt-out til å se alle
  // (lenkbar/delbar URL for "vis alle"). Speiler pre-VIB-1304 default (som
  // var AV) bevisst annerledes — dette er den faktiske fiksen.
  const onlyMine = searchParams.get("mine") !== "0";
  const nyttParam = searchParams.get("nytt");

  useEffect(() => {
    if (nyttParam !== null) setNewDialogOpen(true);
  }, [nyttParam]);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      const target = e.target as HTMLElement;
      if (target.tagName === "INPUT" || target.tagName === "TEXTAREA" || target.isContentEditable) {
        return;
      }
      if (e.key === "n" || e.key === "N") {
        e.preventDefault();
        setNewDialogOpen(true);
      }
    }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, []);

  function handleNewDialogChange(open: boolean) {
    setNewDialogOpen(open);
    if (!open && searchParams.has("nytt")) {
      const next = new URLSearchParams(searchParams);
      next.delete("nytt");
      setSearchParams(next, { replace: true });
    }
  }

  function toggleOnlyMine() {
    const next = new URLSearchParams(searchParams);
    if (onlyMine) next.set("mine", "0");
    else next.delete("mine");
    setSearchParams(next, { replace: true });
  }

  const filtered = useMemo(
    () => filterOffers(offers ?? [], tab, query, onlyMine, activeUser?.id ?? null),
    [offers, tab, query, onlyMine, activeUser?.id],
  );

  // Boardet grupperer selv per status (draft/sent/expired/accepted/lost) —
  // status-tabsene gjelder kun tabellvisningen. Samme søk/"Mine tilbud"-filter.
  const boardOffers = useMemo(
    () => filterOffers(offers ?? [], "all", query, onlyMine, activeUser?.id ?? null),
    [offers, query, onlyMine, activeUser?.id],
  );

  return (
    <div className="page-container animate-fade-in">
      <div className="flex items-center justify-between mb-6 gap-4 flex-wrap">
        <h1 className="font-display font-semibold text-[clamp(28px,3vw,40px)] tracking-[-0.01em] flex items-center gap-2">
          <SrIcon name="file-text" className="h-6 w-6"  />
          Tilbud
        </h1>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={() => setImportDialogOpen(true)}>
            <SrIcon name="upload" className="w-4 h-4 mr-2" />
            Nytt tilbud fra import
          </Button>
          <Button onClick={() => setNewDialogOpen(true)}>
            <SrIcon name="plus" className="w-4 h-4 mr-2"  />
            Nytt tilbud
          </Button>
        </div>
      </div>

      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-3 mb-4 sm:items-center">
            {pipelineEnabled && (
              <div className="flex items-center gap-0.5 rounded-md border p-0.5 shrink-0">
                <Button
                  type="button"
                  variant={view === "table" ? "default" : "ghost"}
                  size="sm"
                  className="h-8"
                  onClick={() => setView("table")}
                >
                  Tabell
                </Button>
                <Button
                  type="button"
                  variant={view === "board" ? "default" : "ghost"}
                  size="sm"
                  className="h-8"
                  onClick={() => setView("board")}
                >
                  Board
                </Button>
              </div>
            )}
            {view === "table" && (
              <Tabs value={tab} onValueChange={(v) => setTab(v as TabKey)}>
                <TabsList>
                  {TABS.map((t) => (
                    <TabsTrigger key={t.key} value={t.key}>
                      {t.label}
                    </TabsTrigger>
                  ))}
                </TabsList>
              </Tabs>
            )}
            <Button
              type="button"
              variant={onlyMine ? "default" : "outline"}
              size="sm"
              onClick={toggleOnlyMine}
              disabled={!activeUser?.id}
              className={cn("h-9", onlyMine && "shadow-sm")}
              aria-pressed={onlyMine}
              title={
                activeUser?.id
                  ? "Vis bare tilbud du har opprettet"
                  : "Logg inn for å filtrere på dine tilbud"
              }
            >
              <SrIcon name="user" className="w-3.5 h-3.5 mr-1.5" />
              Mine tilbud
            </Button>
            <Input
              placeholder="Søk tittel eller kunde…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className="sm:max-w-xs sm:ml-auto"
            />
          </div>

          {view === "board" && pipelineEnabled ? (
            <OfferPipelineBoard offers={boardOffers} />
          ) : isLoading ? (
            <div className="flex justify-center py-8">
              <SrIcon name="loader2" className="h-5 w-5 animate-spin text-muted-foreground"  />
            </div>
          ) : filtered.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-8">
              Ingen tilbud funnet. Opprett ditt første tilbud med knappen øverst.
            </p>
          ) : (
            <>
              {/* Mobile cards (under md) */}
              <div className="md:hidden space-y-2">
                {filtered.map((o) => (
                  <Link
                    key={o.id}
                    to={`/tilbud/${o.id}`}
                    className="block border rounded-md p-3 bg-background hover:bg-muted/30 transition-colors"
                  >
                    <div className="flex items-start justify-between gap-2 mb-1">
                      <div className="min-w-0 flex-1">
                        <p className="font-medium text-sm truncate">{o.title}</p>
                        <p className="text-xs text-muted-foreground truncate">
                          {o.customer?.name ?? o.customer_name ?? "—"}
                        </p>
                      </div>
                      <OfferStatusBadge status={o.status} />
                    </div>
                    <div className="flex items-center justify-between text-xs text-muted-foreground mt-2">
                      <span className="tabular-nums">{formatNOK(o.total_amount)}</span>
                      <span>{formatDate(o.created_at)}</span>
                    </div>
                    {pipelineEnabled && (
                      <div className="flex items-center justify-between text-xs text-muted-foreground mt-1">
                        <OfferProbabilityBadge probability={o.probability} />
                        <span>{o.valid_until ? `Gyldig til ${formatDate(o.valid_until)}` : ""}</span>
                      </div>
                    )}
                  </Link>
                ))}
              </div>

              {/* Desktop table (md and up) */}
              <div className="hidden md:block overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-border/50 text-xs uppercase tracking-wider text-muted-foreground">
                      <th className="text-left py-2 px-2 font-medium">Tittel</th>
                      <th className="text-left py-2 px-2 font-medium">Kunde</th>
                      <th className="text-left py-2 px-2 font-medium w-24">Status</th>
                      {pipelineEnabled && (
                        <>
                          <th className="text-left py-2 px-2 font-medium w-36">Kategori</th>
                          <th className="text-left py-2 px-2 font-medium w-28">Gyldig til</th>
                        </>
                      )}
                      <th className="text-right py-2 px-2 font-medium w-32">Totalsum</th>
                      <th className="text-left py-2 px-2 font-medium w-32">Opprettet</th>
                      <th className="text-left py-2 px-2 font-medium w-32">Av</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map((o) => (
                      <ClickableRow
                        key={o.id}
                        variant="link"
                        className="border-b border-border/30 last:border-0 hover:bg-muted/20"
                        onActivate={() => navigate(`/tilbud/${o.id}`)}
                      >
                        <td className="py-2 px-2 font-medium">{o.title}</td>
                        <td className="py-2 px-2">{o.customer?.name ?? o.customer_name ?? "—"}</td>
                        <td className="py-2 px-2">
                          <OfferStatusBadge status={o.status} />
                        </td>
                        {pipelineEnabled && (
                          <>
                            <td className="py-2 px-2">
                              <OfferProbabilityBadge probability={o.probability} />
                            </td>
                            <td className="py-2 px-2 text-muted-foreground">
                              {o.valid_until ? formatDate(o.valid_until) : "—"}
                            </td>
                          </>
                        )}
                        <td className="py-2 px-2 text-right">{formatNOK(o.total_amount)}</td>
                        <td className="py-2 px-2 text-muted-foreground">{formatDate(o.created_at)}</td>
                        <td className="py-2 px-2 text-muted-foreground">{o.creator?.full_name ?? "—"}</td>
                      </ClickableRow>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      <NewOfferDialog
        open={newDialogOpen}
        onOpenChange={handleNewDialogChange}
        initialSize={nyttParam || undefined}
      />
      <ImportOfferDialog open={importDialogOpen} onOpenChange={setImportDialogOpen} />
    </div>
  );
}
